import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

async function registarSessao(userId: string, userEmail: string, userName: string, action: 'login' | 'logout') {
  try {
    // Obter municipio_id do perfil
    const { data: profileData } = await supabase
      .from('profiles')
      .select('municipio_id')
      .eq('id', userId)
      .maybeSingle();

    let municipioNome: string | null = null;
    if (profileData?.municipio_id) {
      const { data: mun } = await supabase
        .from('municipios')
        .select('nome')
        .eq('id', profileData.municipio_id)
        .maybeSingle();
      municipioNome = mun?.nome ?? null;
    }

    await supabase.from('session_logs').insert({
      user_id: userId,
      user_email: userEmail,
      user_name: userName,
      municipio_id: profileData?.municipio_id ?? null,
      municipio_nome: municipioNome,
      action,
      user_agent: navigator.userAgent.slice(0, 200),
    });
  } catch {
    // Silencioso — não bloquear o login por falha no log
  }
}

export type UserRole = 'super_admin' | 'admin' | 'viewer';

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  created_at: string;
  municipio_id: string | null;
  municipio_nome: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const fetchingRef = useRef(false);

  const fetchProfile = async (userId: string): Promise<void> => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setProfileLoading(true);

    // Timeout absoluto de 4 segundos
    const safetyTimer = setTimeout(() => {
      setProfileLoading(false);
      fetchingRef.current = false;
    }, 4000);

    try {
      // Query direta sem JOIN (não há FK entre profiles e municipios)
      const { data } = await supabase
        .from('profiles')
        .select('id, email, full_name, role, created_at, municipio_id')
        .eq('id', userId)
        .maybeSingle();

      if (data) {
        let municipioNome: string | null = null;
        if (data.municipio_id) {
          const { data: mun } = await supabase
            .from('municipios')
            .select('nome')
            .eq('id', data.municipio_id)
            .maybeSingle();
          municipioNome = mun?.nome ?? null;
        }
        setProfile({
          id: data.id,
          email: data.email,
          full_name: data.full_name,
          role: data.role,
          created_at: data.created_at,
          municipio_id: data.municipio_id,
          municipio_nome: municipioNome,
        });
      } else {
        setProfile(null);
      }
    } catch {
      setProfile(null);
    } finally {
      clearTimeout(safetyTimer);
      setProfileLoading(false);
      fetchingRef.current = false;
    }
  };

  useEffect(() => {
    let mounted = true;

    // Timeout de segurança para o loading inicial: 4 segundos
    const loadingTimer = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 4000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (!mounted) return;

      // Limpar sessão inválida (refresh token expirado/inválido)
      if (event === 'TOKEN_REFRESHED' && !s) {
        supabase.auth.signOut();
        return;
      }

      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
      clearTimeout(loadingTimer);

      if (s?.user) {
        fetchingRef.current = false;
        fetchProfile(s.user.id);
        // Registar login apenas no evento SIGNED_IN (não em INITIAL_SESSION)
        if (event === 'SIGNED_IN') {
          registarSessao(
            s.user.id,
            s.user.email ?? '',
            s.user.user_metadata?.full_name ?? '',
            'login'
          );
        }
      } else {
        setProfile(null);
        setProfileLoading(false);
        fetchingRef.current = false;
      }
    });

    // Obter sessão atual imediatamente
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return;
      if (!s) {
        setLoading(false);
        clearTimeout(loadingTimer);
      }
    });

    return () => {
      mounted = false;
      clearTimeout(loadingTimer);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    return { error: null };
  };

  const signOut = async () => {
    if (user) {
      await registarSessao(user.id, user.email ?? '', profile?.full_name ?? '', 'logout');
    }
    setProfile(null);
    fetchingRef.current = false;
    await supabase.auth.signOut();
  };

  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const isSuperAdmin = profile?.role === 'super_admin';

  return (
    <AuthContext.Provider value={{
      user, session, profile, loading, profileLoading,
      signIn, signOut, isAdmin, isSuperAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
